import type { InitializedSeasonPassConfig, SeasonPassInfo } from '../types';
import type { ResourceMap } from '../../../app/resources/types';
import { InventoryItem } from '../../../app/resources/types';
import { addKeyNumberRecords } from '../../../utils/misc-utils';

export function getNotCollectedTierRewardsAmount(config: InitializedSeasonPassConfig, collectedTokens: number, ignorePremiumPassStatus: boolean): number {
  const lastReachedNodeIndex = getLastReachedNodeIndex(config, collectedTokens);
  return config.tiers.slice(0, lastReachedNodeIndex + 1).reduce((acc, tier) => {
    let notCollectedInTier = 0;

    const shouldCountPremium = ignorePremiumPassStatus ? true : config.premiumPurchased;
    if (shouldCountPremium && !tier.premiumCollected) notCollectedInTier += 1;
    if (!tier.freeCollected) notCollectedInTier += 1;

    return acc + notCollectedInTier;
  }, 0);
}

export function getSeasonPassId(config: InitializedSeasonPassConfig): string {
  return `seasonPass_${config.id}_${config.endTime}`;
}

export function getAllPremiumRewards(config: InitializedSeasonPassConfig): ResourceMap {
  const out: ResourceMap = {};
  config.tiers.forEach((tier) => {
    addKeyNumberRecords(out, tier.rewardsPremium);
  });
  return out;
}

export function getNotCollectedRewards(
  config: InitializedSeasonPassConfig,
  collectedTokens: number,
): { premium: ResourceMap; free: ResourceMap; all: ResourceMap } {
  const lastReachedNodeIndex = getLastReachedNodeIndex(config, collectedTokens);

  const results: { premium: ResourceMap; free: ResourceMap; all: ResourceMap } = { premium: {}, free: {}, all: {} };

  config.tiers.slice(0, lastReachedNodeIndex + 1).forEach((tier) => {
    if (!tier.premiumCollected) {
      addKeyNumberRecords(results.premium, tier.rewardsPremium);
      addKeyNumberRecords(results.all, tier.rewardsPremium);
    }

    if (!tier.freeCollected) {
      addKeyNumberRecords(results.free, tier.rewardsFree);
      addKeyNumberRecords(results.all, tier.rewardsFree);
    }
  });

  const vaultAmount = getSeasonPassVaultAmount(config, collectedTokens);

  if (vaultAmount > 0) {
    addKeyNumberRecords(results.premium, { [InventoryItem.Coin]: vaultAmount });
    addKeyNumberRecords(results.all, { [InventoryItem.Coin]: vaultAmount });
  }

  return results;
}

export function getLastReachedNodeIndex(config: InitializedSeasonPassConfig, collectedTokens: number): number {
  let tokens = collectedTokens;
  const index =
    config.tiers.findIndex((tier) => {
      tokens -= tier.tokensReq;
      return tokens < 0;
    }) - 1;
  return index > -1 ? index : config.tiers.length - 1;
}

export function getCurrentNodeProgress(
  config: InitializedSeasonPassConfig,
  collectedTokens: number,
): { nodeIndex: number; current: number; max: number } | null {
  const lastCollectedNodeIndex = getLastReachedNodeIndex(config, collectedTokens);
  if (lastCollectedNodeIndex === config.tiers.length - 1) return null;

  const nextTokenIndex = lastCollectedNodeIndex + 1;
  const usedTokens = config.tiers.slice(0, nextTokenIndex).reduce((acc, { tokensReq }) => acc + tokensReq, 0);

  return {
    nodeIndex: nextTokenIndex,
    current: collectedTokens - usedTokens,
    max: config.tiers[nextTokenIndex].tokensReq,
  };
}

export function getCurrentVaultProgress(
  config: InitializedSeasonPassConfig,
  collectedTokens: number,
): { current: number; max: number } {
  return {
    current: getVaultTokens(config, collectedTokens) % config.vault.tokensToIncrease,
    max: config.vault.tokensToIncrease,
  };
}

export function areGiftsInTierCollected(config: InitializedSeasonPassConfig, tierIndex: number): boolean {
  return config.tiers[tierIndex].freeCollected && config.tiers[tierIndex].premiumCollected;
}

export function getVaultTokens(config: InitializedSeasonPassConfig, collectedTokens: number): number {
  const tiersTokens = config.tiers.reduce((acc, tier) => acc + tier.tokensReq, 0);
  return Math.max(0, collectedTokens - tiersTokens);
}

export function getSeasonPassVaultAmount(config: InitializedSeasonPassConfig, collectedTokens: number): number {
  return (
    Math.floor(getVaultTokens(config, collectedTokens) / config.vault.tokensToIncrease) * config.vault.coinIncrease
  );
}

export function isSeasonPassRewardCollected(
  config: InitializedSeasonPassConfig,
  tierIndex: number,
  premium: boolean,
): boolean {
  return config.tiers[tierIndex][premium ? 'premiumCollected' : 'freeCollected'];
}

export function isSeasonPassRewardProgressionUnlocked(config: InitializedSeasonPassConfig, tierIndex: number): boolean {
  const lastIndexNode = getLastReachedNodeIndex(config, config.tokensCollected);
  return tierIndex <= lastIndexNode;
}

export function getSeasonPassInfo(config: InitializedSeasonPassConfig): SeasonPassInfo {
  return {
    eventId: getSeasonPassId(config),
    tokens: config.tokensCollected,
  };
}
